import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { trackReferralLinkClick, trackPageView } from './lib/analytics';
import { setReferralCode, normalizeReferralCode, displayReferralCode } from './lib/utils';
import ToastContainer from './components/ToastContainer';
import { APP_ROUTES } from './lib/appRoutes';
import { useGeoStore } from './store/geoStore';
import { useNaturesStore } from './store/naturesStore';
// @module:app-imports — 選用組件的全域組件 import 由 compose 注入於此行之下
import LiveFunnelBot from './components/LiveFunnelBot';
import ProductRecommendPopup from './components/ProductRecommendPopup';
import GeoNoticePopup from './components/GeoNoticePopup';

function RefCapture() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Support ?r= (new short form), ?ref= (legacy), and ?gs= (legacy)
    const rawRef = params.get('r') || params.get('ref') || params.get('gs');

    if (rawRef) {
      const ref = normalizeReferralCode(rawRef);
      if (/^R-[A-Z0-9]{6}$/i.test(ref)) {
        const existing = localStorage.getItem('x1493_referral_code');
        if (!existing) {
          setReferralCode(ref);
        }
        trackReferralLinkClick(displayReferralCode(ref), location.pathname);

        // Clean URL without reloading
        const url = new URL(window.location.href);
        url.searchParams.delete('r');
        url.searchParams.delete('ref');
        url.searchParams.delete('gs');
        navigate(url.pathname + url.search, { replace: true });
      }
    }
  }, [location, navigate]);

  return null;
}

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search]);

  return null;
}

// Fix trailing-slash white screen: normalize /admin/ -> /admin before Router reads URL
const initialPathname = typeof window !== 'undefined' ? window.location.pathname : '';
if (initialPathname.length > 1 && initialPathname.endsWith('/')) {
  const normalized = initialPathname.slice(0, -1) + window.location.search + window.location.hash;
  window.history.replaceState(null, '', normalized);
}

function App() {
  // 進站即讀 IP 所在地（營銷提示用；store 內建訪問級快取＋服務端寫 visit_logs）
  useEffect(() => {
    useGeoStore.getState().load();
    useNaturesStore.getState().load(); // shipping-zones v2 定性映射（🇭🇰 badge 單一數據源）
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <RefCapture />
      <PageViewTracker />
      <ToastContainer />
      {/* @module:global-components — 選用組件的全域組件（如進站彈窗／直播漏斗 bot）由 compose 注入於此行之下（JSX 兄弟位＝多組件並存合法；未注入＝零渲染） */}
        <LiveFunnelBot />
        <ProductRecommendPopup />
        <GeoNoticePopup />
      <Routes>
        {APP_ROUTES.map((r) => (
          <Route key={r.path} path={r.path} element={r.element} />
        ))}
      </Routes>
    </Router>
  );
}

export default App;
